import { privateDefaultPalette, publicPalette } from './config';

/**
 * Shelter admin **Customization** — predefined brand palettes (Settings → Customization).
 *
 * **How to add a palette:** append to `SHELTER_PALETTE_CATALOG` with a unique `id`, `label`,
 * `swatch` (two hex values for the diagonal tile), and `tokens.light` / `tokens.dark`
 * (`--theme-*` CSS custom properties). Match Figma order left-to-right, top-to-bottom.
 *
 * **How to tweak colors:** edit the `tokens` maps for that entry only; avoid changing keys
 * unless you also update `applyPrivateShelterTheme()` in `privateShelterBranding.ts`.
 *
 * Full-app theming applies these variables on `[data-app-theme="private"]` via
 * `PrivateAppThemeRoot` (see `privateShelterBranding.ts`).
 */
export type ShelterPaletteId =
  | 'default'
  | 'purple-indigo'
  | 'forest-teal'
  | 'violet-magenta'
  | 'royal-cyan'
  | 'brown-amber'
  | 'crimson-maroon'
  | 'evergreen-slate'
  | 'deep-slate';

/** CSS variables applied on the private app root (subset mirrors `ds-authenticated.css` bridge). */
export type PrivateThemeCssVars = Record<string, string>;

export type ShelterPaletteDefinition = {
  id: Exclude<ShelterPaletteId, 'default'>;
  label: string;
  /** Diagonal swatch (top-left / bottom-right). */
  swatch: { primary: string; secondary: string };
  tokens: {
    light: PrivateThemeCssVars;
    dark: PrivateThemeCssVars;
  };
};

/** Default = design-system stylesheet only (no inline overrides on app root). */
export const DEFAULT_SHELTER_PALETTE_ID: ShelterPaletteId = 'default';

/** Explicit light defaults for isolated preview when palette is “default”. */
export const PRIVATE_LIGHT_DEFAULT_VARS: PrivateThemeCssVars = {
  '--theme-background': privateDefaultPalette.background,
  '--theme-foreground': privateDefaultPalette.foreground,
  '--theme-muted': privateDefaultPalette.muted,
  '--theme-muted-foreground': privateDefaultPalette.mutedForeground,
  '--theme-border': privateDefaultPalette.border,
  '--theme-card': privateDefaultPalette.card,
  '--theme-card-foreground': privateDefaultPalette.cardForeground,
  '--theme-primary-foreground': privateDefaultPalette.primaryForeground,
  '--theme-accent': privateDefaultPalette.accent,
  '--theme-accent-foreground': privateDefaultPalette.accentForeground,
  '--theme-destructive': privateDefaultPalette.destructive,
  '--theme-destructive-foreground': privateDefaultPalette.destructiveForeground,
  '--theme-header': privateDefaultPalette.header,
  '--theme-header-foreground': privateDefaultPalette.headerForeground,
  '--theme-header-muted': privateDefaultPalette.headerMuted,
  '--theme-highlight': privateDefaultPalette.highlight,
  '--theme-highlight-foreground': privateDefaultPalette.highlightForeground,
  '--theme-success': privateDefaultPalette.success,
  '--theme-success-foreground': privateDefaultPalette.successForeground,
  '--theme-popover': privateDefaultPalette.popover,
  '--theme-popover-foreground': privateDefaultPalette.popoverForeground,
  '--theme-primary-50': privateDefaultPalette.primary[50],
  '--theme-primary-100': privateDefaultPalette.primary[100],
  '--theme-primary-200': privateDefaultPalette.primary[200],
  '--theme-primary-300': privateDefaultPalette.primary[300],
  '--theme-primary-400': privateDefaultPalette.primary[400],
  '--theme-primary-500': privateDefaultPalette.primary[500],
  '--theme-primary-600': privateDefaultPalette.primary[600],
  '--theme-primary-700': privateDefaultPalette.primary[700],
  '--theme-primary-800': privateDefaultPalette.primary[800],
  '--theme-primary-900': privateDefaultPalette.primary[900],
  '--theme-primary-950': privateDefaultPalette.primary[950],
};

function primaryScale(main: string, dark: string, darker: string): PrivateThemeCssVars {
  return {
    '--theme-primary-50': '#fdf4ff',
    '--theme-primary-100': '#fae8ff',
    '--theme-primary-200': '#f5d0fe',
    '--theme-primary-300': '#f0abfc',
    '--theme-primary-400': '#e879f9',
    '--theme-primary-500': main,
    '--theme-primary-600': main,
    '--theme-primary-700': dark,
    '--theme-primary-800': darker,
    '--theme-primary-900': darker,
    '--theme-primary-950': darker,
  };
}

const lightSurfaces = (accent: string): PrivateThemeCssVars => ({
  '--theme-background': '#ffffff',
  '--theme-foreground': '#1c1526',
  '--theme-muted': '#f7f5fa',
  '--theme-muted-foreground': '#4a3f5c',
  '--theme-border': '#eeeeee',
  '--theme-card': '#ffffff',
  '--theme-card-foreground': '#1c1526',
  '--theme-primary-foreground': '#ffffff',
  '--theme-accent': accent,
  '--theme-accent-foreground': '#1c1526',
  '--theme-destructive': '#dd3730',
  '--theme-destructive-foreground': '#ffffff',
  '--theme-header': '#f7f5fa',
  '--theme-header-foreground': '#1c1526',
  '--theme-header-muted': '#4a3f5c',
  '--theme-highlight': '#f47521',
  '--theme-highlight-foreground': '#ffffff',
  '--theme-success': '#17a83d',
  '--theme-success-foreground': '#ffffff',
  '--theme-popover': 'rgba(58, 42, 74, 0.95)',
  '--theme-popover-foreground': '#ffffff',
});

const darkSurfaces = (card: string, muted: string): PrivateThemeCssVars => ({
  '--theme-background': '#2c1f3c',
  '--theme-foreground': '#ffffff',
  '--theme-muted': muted,
  '--theme-muted-foreground': '#cfc6d8',
  '--theme-border': '#5a4d6b',
  '--theme-card': card,
  '--theme-card-foreground': '#ffffff',
  '--theme-primary-foreground': '#ffffff',
  '--theme-accent': 'rgba(255,255,255,0.08)',
  '--theme-accent-foreground': '#ffffff',
  '--theme-destructive': '#e5484d',
  '--theme-destructive-foreground': '#ffffff',
  '--theme-header': '#34254a',
  '--theme-header-foreground': '#ffffff',
  '--theme-header-muted': '#cfc6d8',
  '--theme-highlight': '#f47521',
  '--theme-highlight-foreground': '#ffffff',
  '--theme-success': '#17a83d',
  '--theme-success-foreground': '#ffffff',
  '--theme-popover': 'rgba(58, 42, 74, 0.95)',
  '--theme-popover-foreground': '#ffffff',
});

/**
 * Friends for Life built-in adoption quiz palette (`paletteId: "default"`).
 * Used when no shelter theme is set and when a shelter explicitly picks “Built-in default”.
 * Not part of `SHELTER_PALETTE_CATALOG` so it never collides with Purple / Indigo.
 */
export const BUILTIN_ADOPT_QUIZ_DEFAULT = {
  swatch: {
    primary: publicPalette.primary[600],
    secondary: publicPalette.highlight,
  },
  tokens: {
    light: PRIVATE_LIGHT_DEFAULT_VARS,
    dark: {
      ...darkSurfaces(publicPalette.card, publicPalette.muted),
      ...primaryScale(
        privateDefaultPalette.primary[600],
        privateDefaultPalette.primary[700],
        privateDefaultPalette.primary[800],
      ),
    },
  },
} as const;

export const SHELTER_PALETTE_CATALOG: ShelterPaletteDefinition[] = [
  {
    id: 'purple-indigo',
    label: 'Purple / Indigo',
    swatch: { primary: '#7c3aed', secondary: '#312e81' },
    tokens: {
      light: {
        ...lightSurfaces('#ede9fe'),
        ...primaryScale('#7c3aed', '#5b21b6', '#4c1d95'),
      },
      dark: {
        ...darkSurfaces('#352a5c', '#3d3258'),
        ...primaryScale('#a78bfa', '#7c3aed', '#5b21b6'),
      },
    },
  },
  {
    id: 'forest-teal',
    label: 'Forest / Teal',
    swatch: { primary: '#166534', secondary: '#0f766e' },
    tokens: {
      light: {
        ...lightSurfaces('#ecfdf5'),
        ...primaryScale('#15803d', '#14532d', '#134e4a'),
      },
      dark: {
        ...darkSurfaces('#1e3a2a', '#2a3d3a'),
        ...primaryScale('#22c55e', '#15803d', '#14532d'),
      },
    },
  },
  {
    id: 'violet-magenta',
    label: 'Violet / Magenta',
    swatch: { primary: '#8b5cf6', secondary: '#db2777' },
    tokens: {
      light: {
        ...lightSurfaces('#fce7f3'),
        ...primaryScale('#c026d3', '#a21caf', '#86198f'),
      },
      dark: {
        ...darkSurfaces('#3b2a55', '#4a3058'),
        ...primaryScale('#e879f9', '#c026d3', '#86198f'),
      },
    },
  },
  {
    id: 'royal-cyan',
    label: 'Royal / Cyan',
    swatch: { primary: '#1d4ed8', secondary: '#0891b2' },
    tokens: {
      light: {
        ...lightSurfaces('#e0f2fe'),
        ...primaryScale('#2563eb', '#1e40af', '#1e3a8a'),
      },
      dark: {
        ...darkSurfaces('#1e2f4a', '#2a3f52'),
        ...primaryScale('#38bdf8', '#2563eb', '#1e3a8a'),
      },
    },
  },
  {
    id: 'brown-amber',
    label: 'Brown / Amber',
    swatch: { primary: '#92400e', secondary: '#d97706' },
    tokens: {
      light: {
        ...lightSurfaces('#fffbeb'),
        ...primaryScale('#b45309', '#92400e', '#78350f'),
      },
      dark: {
        ...darkSurfaces('#3a2a1e', '#4a3a28'),
        ...primaryScale('#fbbf24', '#d97706', '#92400e'),
      },
    },
  },
  {
    id: 'crimson-maroon',
    label: 'Crimson / Maroon',
    swatch: { primary: '#be123c', secondary: '#7f1d1d' },
    tokens: {
      light: {
        ...lightSurfaces('#ffe4e6'),
        ...primaryScale('#e11d48', '#be123c', '#881337'),
      },
      dark: {
        ...darkSurfaces('#3a1f28', '#451c22'),
        ...primaryScale('#fb7185', '#e11d48', '#881337'),
      },
    },
  },
  {
    id: 'evergreen-slate',
    label: 'Evergreen / Slate',
    swatch: { primary: '#14532d', secondary: '#334155' },
    tokens: {
      light: {
        ...lightSurfaces('#ecfdf5'),
        ...primaryScale('#166534', '#14532d', '#0f172a'),
      },
      dark: {
        ...darkSurfaces('#1e2a22', '#2d3748'),
        ...primaryScale('#4ade80', '#166534', '#0f172a'),
      },
    },
  },
  {
    id: 'deep-slate',
    label: 'Deep / Slate',
    swatch: { primary: '#4c1d95', secondary: '#1e293b' },
    tokens: {
      light: {
        ...lightSurfaces('#f5f3ff'),
        ...primaryScale('#6d28d9', '#5b21b6', '#1e293b'),
      },
      dark: {
        ...darkSurfaces('#2e1064', '#334155'),
        ...primaryScale('#a78bfa', '#6d28d9', '#312e81'),
      },
    },
  },
];

export function getPaletteDefinition(
  id: ShelterPaletteId,
): ShelterPaletteDefinition | null {
  if (id === 'default') return null;
  return SHELTER_PALETTE_CATALOG.find((p) => p.id === id) ?? null;
}

export function isKnownShelterPaletteId(id: unknown): id is ShelterPaletteId {
  if (id === DEFAULT_SHELTER_PALETTE_ID) return true;
  return (
    typeof id === 'string' &&
    SHELTER_PALETTE_CATALOG.some((p) => p.id === id)
  );
}

export function resolvePrivateThemeVars(
  paletteId: ShelterPaletteId,
  mode: 'light' | 'dark',
): PrivateThemeCssVars | null {
  if (paletteId === 'default') {
    // Authenticated admin shell: light “default” = design-system stylesheet only.
    if (mode === 'light') return null;
    return BUILTIN_ADOPT_QUIZ_DEFAULT.tokens.dark;
  }
  const def = getPaletteDefinition(paletteId);
  if (!def) return null;
  return mode === 'light' ? def.tokens.light : def.tokens.dark;
}

/** Always returns a full map (preview + public adopt quiz). */
export function resolvePrivateThemeVarsForPreview(
  paletteId: ShelterPaletteId,
  mode: 'light' | 'dark',
): PrivateThemeCssVars {
  if (paletteId === 'default') {
    return mode === 'light'
      ? BUILTIN_ADOPT_QUIZ_DEFAULT.tokens.light
      : BUILTIN_ADOPT_QUIZ_DEFAULT.tokens.dark;
  }
  const def = getPaletteDefinition(paletteId);
  if (!def) return PRIVATE_LIGHT_DEFAULT_VARS;
  return mode === 'light' ? def.tokens.light : def.tokens.dark;
}

/** Stored on `settings.adoptionQuizTheme.mode` — keep in sync with backend `adoptionQuizTheme.ts`. */
export const ADOPTION_QUIZ_THEME_MODES = ['light', 'default', 'deep'] as const;
export type AdoptionQuizThemeMode = (typeof ADOPTION_QUIZ_THEME_MODES)[number];

/** Public quiz canvas for the “Dark” row in settings (deeper than catalog `dark` tokens). */
export const ADOPTION_QUIZ_DEEP_DARK_BACKGROUND = '#101114';

export function isAdoptionQuizThemeMode(m: unknown): m is AdoptionQuizThemeMode {
  return (
    typeof m === 'string' &&
    (ADOPTION_QUIZ_THEME_MODES as readonly string[]).includes(m)
  );
}

/**
 * Legacy `mode: "dark"` meant catalog dark tokens — maps to `"default"` after the
 * Light / Dark / Default product split (`"deep"` is the new darker canvas).
 */
export function normalizeAdoptionQuizThemeMode(
  mode: unknown,
): AdoptionQuizThemeMode | null {
  if (mode === 'light' || mode === 'default' || mode === 'deep') return mode;
  if (mode === 'dark') return 'default';
  return null;
}

/** CSS vars for the public adoption quiz (Customization preview + `/adopt/…`). */
export function resolveAdoptionQuizThemeCssVars(
  paletteId: ShelterPaletteId,
  mode: AdoptionQuizThemeMode,
): PrivateThemeCssVars {
  if (mode === 'light') {
    return resolvePrivateThemeVarsForPreview(paletteId, 'light');
  }
  if (mode === 'default') {
    return resolvePrivateThemeVarsForPreview(paletteId, 'dark');
  }
  const base = resolvePrivateThemeVarsForPreview(paletteId, 'dark');
  return { ...base, '--theme-background': ADOPTION_QUIZ_DEEP_DARK_BACKGROUND };
}
